import React, { useState } from 'react';
import { FaStar, FaTimes } from 'react-icons/fa';

interface FeedbackFormProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

interface FeedbackData {
  type: 'general' | 'bug' | 'feature' | 'complaint' | 'compliment';
  rating: number;
  title: string;
  description: string;
  email: string;
  name: string;
}

const FeedbackForm: React.FC<FeedbackFormProps> = ({ isOpen, onClose, className = "" }) => {
  const [feedback, setFeedback] = useState<FeedbackData>({
    type: 'general',
    rating: 0,
    title: '',
    description: '',
    email: '',
    name: ''
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const feedbackTypes = [
    { value: 'general', label: 'General Feedback', icon: '💬' },
    { value: 'bug', label: 'Bug Report', icon: '🐛' },
    { value: 'feature', label: 'Feature Request', icon: '💡' },
    { value: 'complaint', label: 'Complaint', icon: '⚠️' },
    { value: 'compliment', label: 'Compliment', icon: '❤️' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit feedback to server
      const response = await fetch('https://ticketreselling-production.up.railway.app/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: feedback.type,
          rating: feedback.rating,
          title: feedback.title,
          description: feedback.description,
          name: feedback.name,
          email: feedback.email,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setFeedback({
          type: 'general',
          rating: 5,
          title: '',
          description: '',
          name: '',
          email: '',
        });
        alert('Thank you for your feedback! It has been submitted successfully.');
      } else {
        throw new Error(result.error || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Feedback submission error:', error);
      alert('Failed to submit feedback. Please try again or contact support directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRatingClick = (rating: number) => {
    setFeedback(prev => ({ ...prev, rating }));
  };

  // Ensure header is visible when form opens
  React.useEffect(() => {
    if (isOpen) {
      const formContent = document.getElementById('feedback-form-content');
      if (formContent) {
        // Force scroll to top to show branding
        setTimeout(() => {
          formContent.scrollTop = 0;
        }, 100);
      }
    }
  }, [isOpen]);

  console.log('FeedbackForm render - isOpen:', isOpen);
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-50">
      <div 
        className={`bg-white rounded-2xl shadow-2xl w-full max-w-sm sm:max-w-md overflow-hidden ${className}`}
        style={{ maxHeight: '95vh', minHeight: '85vh' }}
      >
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(95vh - 6rem)' }} id="feedback-form-content">
            {/* Enhanced Header */}
            <div className="sticky top-0 bg-white z-10 border-b border-gray-200 shadow-sm">
              <div className="flex items-center justify-between p-4 sm:p-6">
                <div className="flex items-baseline space-x-2 sm:space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-[#D6A77A] to-[#FF6B35] rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm">L</span>
                  </div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">LayLow-India</h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100"
                >
                  <FaTimes className="text-lg sm:text-xl" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Enhanced Feedback Type Selection */}
              <div>
                <label className="block text-sm sm:text-base font-semibold text-gray-900 mb-3">
                  What type of feedback is this?
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                  {feedbackTypes.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFeedback(prev => ({ ...prev, type: type.value as any }))}
                      className={`p-3 sm:p-4 rounded-xl border-2 transition-all text-center ${
                        feedback.type === type.value
                          ? 'border-[#D6A77A] bg-[#D6A77A] bg-opacity-10 shadow-md'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="text-2xl sm:text-3xl mb-2">{type.icon}</div>
                      <div className="text-xs sm:text-sm font-medium text-gray-700 leading-tight">{type.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Enhanced Rating Section */}
              <div className="text-center py-2">
                <label className="block text-sm sm:text-base font-semibold text-gray-900 mb-4">
                  How would you rate your experience?
                </label>
                <div className="flex justify-center items-center space-x-2 sm:space-x-3 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => handleRatingClick(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="p-2 transition-all hover:scale-110"
                    >
                      <FaStar
                        className={`text-2xl sm:text-3xl transition-colors ${
                          star <= (hoverRating || feedback.rating)
                            ? 'text-yellow-400'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <p className="text-sm text-gray-500 font-medium">
                  {feedback.rating === 0 && 'Click to rate'}
                  {feedback.rating === 1 && 'Poor'}
                  {feedback.rating === 2 && 'Fair'}
                  {feedback.rating === 3 && 'Good'}
                  {feedback.rating === 4 && 'Very Good'}
                  {feedback.rating === 5 && 'Excellent'}
                </p>
              </div>

              {/* Enhanced Title Input */}
              <div>
                <label className="block text-sm sm:text-base font-semibold text-gray-900 mb-2">
                  Brief Title *
                </label>
                <input
                  type="text"
                  required
                  value={feedback.title}
                  onChange={(e) => setFeedback(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Summarize your feedback in a few words"
                  className="w-full px-4 py-3 sm:py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D6A77A] focus:border-transparent transition-colors text-gray-900 bg-white text-base sm:text-lg placeholder-gray-500"
                />
              </div>

              {/* Enhanced Description Input */}
              <div>
                <label className="block text-sm sm:text-base font-semibold text-gray-900 mb-2">
                  Detailed Description *
                </label>
                <textarea
                  required
                  value={feedback.description}
                  onChange={(e) => setFeedback(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Please provide more details about your feedback..."
                  rows={4}
                  className="w-full px-4 py-3 sm:py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D6A77A] focus:border-transparent transition-colors resize-none text-gray-900 bg-white text-base sm:text-lg placeholder-gray-500"
                />
              </div>

              {/* Enhanced Contact Info */}
              <div className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-4">
                <div>
                  <label className="block text-sm sm:text-base font-semibold text-gray-900 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={feedback.name}
                    onChange={(e) => setFeedback(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Your name (optional)"
                    className="w-full px-4 py-3 sm:py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D6A77A] focus:border-transparent transition-colors text-gray-900 bg-white text-base sm:text-lg placeholder-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm sm:text-base font-semibold text-gray-900 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={feedback.email}
                    onChange={(e) => setFeedback(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="your@email.com (optional)"
                    className="w-full px-4 py-3 sm:py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D6A77A] focus:border-transparent transition-colors text-gray-900 bg-white text-base sm:text-lg placeholder-gray-500"
                  />
                </div>
              </div>

              {/* Enhanced Submit Buttons */}
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-semibold text-base sm:text-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !feedback.title || !feedback.description}
                  className="flex-1 px-6 py-4 bg-[#D6A77A] text-white rounded-xl hover:bg-[#b98a5e] transition-colors font-semibold text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                </button>
              </div>
            </form>
        </div>
      </div>
    </div>
  );
};

export default FeedbackForm; 